import React, {useEffect, useRef, useState} from 'react';
import {
    IonButtons,
    IonHeader,
    IonItem,
    IonPage,
    IonToast,
    useIonViewDidEnter,
    useIonViewWillEnter
} from '@ionic/react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {RouteComponentProps} from "react-router-dom";
import {
    StyledDataTable,
    StyledIonContent,
    StyledIonTitle,
    StyledIonToolbar
} from '../../../../components/content/content.style';
import {StyledHeaderButton, StyledHeaderButtonImage} from "../../../../components/button/button.style";
import {StyledIonLabel} from "../../../../components/form/input/label.style";
import {StyledIonCheckbox} from "../../../../components/form/input/checkbox.style";
import ArrowLeftImage from '../../../../assets/images/arrow-left.svg';
import CheckImage from "../../../../assets/images/check.svg";
import AvatarImage from "../../../../assets/images/logo/x-black.png";
import VectorImage from '../../../../assets/images/checkMarkGreen.svg';
import {ProposalWorker} from "../../../../models/proposal";
import {getWorkers, postProposal} from "../../../../services/proposal.service";
import {Links} from "../../../links";
import StaticPane from "../../../../components/pane/static-pane.component";
import InvitationUserSkeleton from "./components/invitationUserSkeleton.component";
import WorkerPreviewModal from "../../../../modals/worker/workerPreview.component";
import {Invitation} from "./panes/newInvitationPane.component";
import {b64DecodeUnicode} from "../../../../utils/tools/string";
import {useToast} from "@context/toast.context";

interface RouterProps {
    data?: string | undefined;
}

interface SelectPeopleProps extends RouteComponentProps<RouterProps> {
}

const SelectPeoplePage: React.FC<SelectPeopleProps> = ({match}) => {
    const {t} = useTranslation();
    const history = useHistory();
    const {showToast} = useToast();

    const header = useRef<HTMLIonHeaderElement>(null);
    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);

    const [workers, setWorkers] = useState<ProposalWorker[]>();
    const [selectedPeople, updateSelectedPeople] = useState<bigint[]>([]);
    const [selectedWorker, setSelectedWorker] = useState<ProposalWorker | null>(null);
    const [onlyActiveParam, setOnlyActiveParam] = useState<boolean>(true);

    const handleBack = () => {
        history.goBack();
    };

    const choosePerson = (personId: bigint) => {
        const personIndex = selectedPeople.indexOf(personId);

        if (personIndex === -1) {
            updateSelectedPeople([...selectedPeople, personId]);
        } else {
            selectedPeople.splice(personIndex, 1);
            updateSelectedPeople([...selectedPeople]);
        }
    };

    const handleSave = async () => {
        if (match.params.data) {
            let data = JSON.parse(b64DecodeUnicode(match.params.data)) as Invitation;
            data.workers = selectedPeople;

            await postProposal(data)
                .then(() => {
                    history.push(Links.main + Links.planning + '?planningSelectedOption=1')
                })
                .catch((error) => {
                    let message = t('proposal.error.add');

                    const workersError = error.response.data.errors.children.workers?.errors;
                    if (workersError) {
                        message = t('proposal.error.workersWithoutAvailabilities') + ': ' + workersError[0].split(':')[1]?.trim().replace(/\n/g, ', ');
                    }

                    showToast({
                        message: message,
                        type: 'danger',
                        duration: 3000
                    })

                    console.log(error.response);
                });
        }
    };

    useEffect(() => {
        updateHeight();
        updateSelectedPeople([]);
    }, []);

    useIonViewWillEnter(() => {
        updateSelectedPeople([]);
    })

    const updateHeight = () => {
        if (header.current?.clientHeight == 0) {
            setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    const fetchWorkers = async () => {
        if (match.params.data) {
            let data = JSON.parse(atob(match.params.data)) as Invitation;

            if (data.project) {
                setLoading(true);
                setWorkers([]);
                await getWorkers(data, onlyActiveParam)
                    .then(response => {
                        setWorkers(response.data);
                    });

                setLoading(false);
            }
        }
    }

    const getUserPhoto = (data: ProposalWorker): string => {
        return data?.worker?.user && (data?.worker?.user?.photos || data?.worker?.user?.avatar) ? (data?.worker?.user.photos?.thumbnail ? data?.worker?.user.photos.thumbnail : data?.worker?.user.avatar) : AvatarImage;
    }

    useIonViewDidEnter(() => {
        fetchWorkers()
            .catch((error) => {
                console.error(error);
            });
    });

    useEffect(() => {
        fetchWorkers()
            .catch((error) => {
                console.error(error);
            });
    }, [match.params.data, onlyActiveParam])

    return (
        <IonPage>
            <StyledIonContent>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        <IonButtons slot="start">
                            <StyledHeaderButton onClick={() => handleBack()}>
                                <StyledHeaderButtonImage src={ArrowLeftImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                        <StyledIonTitle>{t("selectPeoplePage.title")}</StyledIonTitle>
                        <IonButtons slot="end">
                            <StyledHeaderButton disabled={selectedPeople.length === 0} onClick={() => handleSave()}>
                                <StyledHeaderButtonImage src={CheckImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                    </StyledIonToolbar>
                </IonHeader>
                <StaticPane topEdge={topEdge} marginTop={40} paddingBottom={52}>
                    <IonItem lines="none">
                        <StyledIonLabel
                            className="ion-text-wrap">{t('selectPeoplePage.onlyActive')}</StyledIonLabel>
                        <StyledIonCheckbox name="onlyActive"
                                           slot="start"
                                           checked={onlyActiveParam}
                                           value={onlyActiveParam}
                                           onIonChange={() => setOnlyActiveParam((prevState) => !prevState)}
                        />
                    </IonItem>
                    <StyledDataTable className="no-margin">
                        {loading &&
                            <>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                                <InvitationUserSkeleton/>
                            </>
                        }
                        {!loading && workers && workers.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1).map((data, i) =>
                            <div key={i}
                                 className="data-row"
                                 onClick={() => choosePerson(data.id)}
                            >
                                <div className={"data-icon-title " + data.status}>
                                    <img className="avatar"
                                         onClick={(e) => {
                                             e.stopPropagation();
                                             setSelectedWorker(data);
                                         }}
                                         src={getUserPhoto(data)}
                                    />{data.name}
                                </div>
                                <div className="data-action">
                                    {
                                        selectedPeople.includes(data.id) && <img src={VectorImage}/>
                                    }
                                </div>
                            </div>)
                        }
                    </StyledDataTable>
                </StaticPane>
            </StyledIonContent>
            <WorkerPreviewModal worker={selectedWorker}
                                isOpen={!!selectedWorker}
                                onClose={() => setSelectedWorker(null)}
            />
        </IonPage>
    );
};

export default SelectPeoplePage;
